/**
 * 接口代理模块
 * @author wangdi
 */

/*===============================================================================================*/

import Hashes from './src/vendor/sha1';
import Ajax from './src/wdAjax.js';
import AjaxAgency from './src/wdAjaxAgency.js';
import {isObject, isArray, isFunction, isString, isUndefined} from "../utils/isWhat";
import {parseURL, buildURL} from "../utils/url";
import {objClone, getObjAttr, objCover, objEqual} from "../utils/objectOpt";

//=========================================================
//  参数说明 & 默认参数
//=========================================================
let counter = 0;
let defaultOptions = {
    //----------------------------------
    // 接口操作对象生成配置
    //----------------------------------
    // 接口配置json
    interfaceConfig: {},

    //----------------------------------
    //  接口数据默认配置
    //----------------------------------
    // 默认域名
    origin: null,


    // 默认loading
    loadingShow() {
        if (window["$Loading"]) window["$Loading"].coverShow.apply(this, arguments);
    },
    loadingHide() {
        if (window["$Loading"]) window["$Loading"].coverHide.apply(this, arguments);
    },

    // xhr send 前钩子函数
    beforeSend: new Function,

    // 成功回调的数据预处理
    dataFilter: null,

    // 默认失败回调
    errCallback: function (msg) {
        if (window["$Dialog"]) window["$Dialog"].notify(msg);
        else console.warn(msg);
    },

    // ajax default
    type: "get",
    dataType: "json",
    contentType: "application/json; charset=utf-8",
    traditional: true,
    jsonp: null,     // jsonpCallback

    // modify header
    fakeHeaders: null
};

/**
 * 接口配置项
 * @typedef {Object} unitInfo
 * @property {string} pathname                  - 接口路径,路径可以是相对路径也可以是绝对路径，按书写方式区分
 * @property {string} [origin=""]               - 接口域名,如果一个接口存在origin配置，则会和pathname进行拼接作为具体路径。
 * @property {string} [type="GET"]              - 操作方法 GET|POST
 * @property {string} [dataType="json"]         - 数据类型 json|jsonp
 * @property {string} [jsonp="jsonpCallback]    - jsonp回调函数名
 * @property {string} [beforeSend=]             - xhr send 前钩子函数
 * @property {string} [loading=""]              - 加载中提示
 * @property {string} [paramsType=""]           - 参数类型
 * @property {function} [paramsFilter=""]       - 参数预处理
 * @property {string} [dataPath=""]             - 数据路径
 * @property {function} [dataFilter=""]         - 数据预处理
 * @property {object} [pathParams=]             - 加载中提示
 * @property {boolean} [merge=false]            - 合并
 * @property {boolean} [queue=false]            - 排队
 * @property {boolean} [single=false]           - 单一最后有效
 * @property {number} [validTime=0]             - 有效时间，单位为秒，可以为浮点数
 * @property {*} [params=null]                  - 接口查询参数，{} [] "" ， 如果没有接口调用时没有对应参数，会使用这里配置的默认配置进行补充
 * @property {string} [name=""]                 - 描述接口名称，参考使用
 * @property {*} [returns=null]                 - 接口成功状态返回数据的参考格式，只是针对数据部分。例如: 返回数据:{ success:true,data:{} } => returns : {} （为data对象）
 * @property {string} [crossOriginSrc=""]       - 接口跨域iframe url
 *
 * @example
 * // 对象模式
 * {
 *      "getWareHouseList": {
 *           name: "全部仓库",
 *           pathname: "/wms/warehouse/getAll",
 *           origin: __origin__,
 *           dataType: "jsonp",
 *           jsonp: 'jsonpCallback',
 *           validTime: 30,   // 仓库列表信息有效期设置为 30s
 *           params: {type:null},
 *           returns: {
 *               "wareHouseList": [
 *                   {
 *                       "id": "8a958a0254600a1b0154600f67560000",
 *                       "no": "ZZ001",													//仓库编号
 *                       "name": "郑州一号仓1111"//仓库名称
 *                   }
 *               ]
 *           }
 *      }
 *      // ...
 * }
 * // 数组模式
 * [
 *      {
 *           // 生成方法名称在这里标记
 *           method:"getWareHouseList",
 *           name: "全部仓库",
 *           pathname: "/wms/warehouse/getAll",
 *           origin: __origin__,
 *           dataType: "jsonp",
 *           jsonp: 'jsonpCallback',
 *           validTime: 30,   // 仓库列表信息有效期设置为 30s
 *           params: {type:null},
 *           returns: {
 *               "wareHouseList": [
 *                   {
 *                       "id": "8a958a0254600a1b0154600f67560000",
 *                       "no": "ZZ001",													//仓库编号
 *                       "name": "郑州一号仓1111"//仓库名称
 *                   }
 *               ]
 *           }
 *      }
 *      // ...
 * ]
 *
 */

// 接口请求装配工具箱
const toolkit = {

    // 生成接口最终配置
    getInterfaceConfig(key, proxyObj) {
        // 接口配置列表
        const configList = proxyObj.$configMap;

        // 预处理optionsList
        function doMixin(config) {
            let result = config;
            if (config.mixins) {
                var mixinTarget = config.mixins;
                if (isString(mixinTarget) || isObject(mixinTarget)) {
                    mixinTarget = [mixinTarget];
                }
                if (isArray(mixinTarget)) {
                    mixinTarget.reverse().map(mixin => {
                        if (isString(mixin)) {
                            return configList[mixin];
                        }
                        if (isObject(mixin)) return mixin;
                        console.error("InterfaceProxy Mixin ERROR.");
                    }).forEach(mixin => {
                        if (mixin.mixins) {
                            // 循环mixin处理
                            // delete mixin.mixins
                            result = objCover(doMixin(mixin), result);
                        } else {
                            result = objCover(objClone(mixin), result);
                        }
                    });
                }
                // 循环mixin处理
                // if (mixinTarget) delete result.mixins;
            }
            return result;
        }

        // 拼装配置列表
        var unitOptions = doMixin(configList[key]);
        var troopOptions = proxyObj.$additionalOptions;
        var finalConfig = objCover(objCover(objClone(defaultOptions), troopOptions), unitOptions);
        finalConfig.$unitOptions = unitOptions;
        finalConfig.$troopOptions = troopOptions;
        finalConfig.$defaultOptions = defaultOptions;
        return finalConfig;
    },
    // 生成Ajax最终配置
    getAjaxConfig(args, interfaceConfig) {
        /*----------------------------------------------------
         支持参数传入格式
         > callback
         > callback,errCallback
         > params,callback,errCallback
         > [pArg1,pArg2..],callback,errCallback
         > 依照Res配置默认构造方法填充未传入参数值
         ----------------------------------------------------*/

        let defaultParams = interfaceConfig.params;
        let captureParams = {};
        let pathParams = interfaceConfig.pathParams;
        let captureCallback = null;
        let captureErrCallback = null;

        // 默认参数值 由接口配置生成
        for (let pn in defaultParams) {
            if (!defaultParams.hasOwnProperty(pn)) continue;
            captureParams[pn] = function (method) {
                let tempParam = method;
                if (isString(method)) {
                    try {
                        eval('tempParam =' + method);
                    } catch (e) {
                        tempParam = method;
                    }
                }
                if (isFunction(method)) tempParam = method();
                return tempParam;
            }(defaultParams[pn]);
        }
        // 支持新的配置类型 {type:String,...}
        Object.keys(captureParams).forEach(key => {
            const value = captureParams[key];
            if (value === Number
                || value === String
                || value === Array
                || value === Boolean
                || value === Function
                || value === Symbol) {
                captureParams[key] = null;
                return;
            }
            if (isObject(value)) {
                if (!isUndefined(value.default)) {
                    captureParams[key] = value.default;
                }else{
                    captureParams[key] = null;
                }
            }
        });
        // 分析函数传入参数列表，提取 params,callback,errCallback
        let multiParamPattern = true;
        let multiParamNum = 0;
        // 根据函数arguments解析参数
        [].forEach.call(args, function (param) {
            // 提取成功与失败回调函数
            if (isFunction(param)) {
                if (/fail|error|errorCallback|errCallback/.test(param.name)) {
                    captureErrCallback = param;
                    return;
                }
                if (/success|callback/.test(param.name)) {
                    captureCallback = param;
                    return;
                }
                if (!captureCallback) {
                    captureCallback = param;
                    return;
                }
                if (!captureErrCallback) {
                    captureErrCallback = param;
                    return;
                }
            }
            // 提取参数列表  单独 {} 配置
            if (isObject(param)) {
                objCover(captureParams, param);
                multiParamPattern = false;
            }
            // 多参数配置
            if (multiParamPattern && !isFunction(param)) {
                let attrNum = 0;
                for (let an in defaultParams) {
                    if (!defaultParams.hasOwnProperty(an)) continue;
                    if (attrNum++ == multiParamNum) {
                        captureParams[an] = param;
                        multiParamNum++;
                        break;
                    }
                }
            }
        });

        // 基本参数处理
        let url = this.loadUrlString(interfaceConfig);
        captureParams = this.paramFilter(captureParams);

        // url path重构 pathParams
        if (pathParams) {
            for (let an in pathParams) {
                if (!pathParams.hasOwnProperty(an)) continue;
                let pathParamValue = getObjAttr(captureParams, an);
                url = url + (/\/$/.test(url) ? '' : '/') + pathParamValue;
                delete captureParams[an];
            }
        }

        // ! 参数过滤器 -------------------------------------------------
        if (isFunction(interfaceConfig.paramsFilter)) {
            captureParams = interfaceConfig.paramsFilter(captureParams);
        }
        // --------------------------------------------------------------

        // 参数表单字符串形式
        if (interfaceConfig.paramsType == "JSON.stringfy") {
            captureParams = JSON.stringify(captureParams);
        }

        // data = jsonString
        if (interfaceConfig.paramsType == "data=jsonString") {
            captureParams = {
                data: JSON.stringify(captureParams)
            };
        }

        // post 处理 将data转化为json字符串
        if (interfaceConfig.type.toLocaleLowerCase() == "post"
            && interfaceConfig.contentType != "application/x-www-form-urlencoded") {
            captureParams = JSON.stringify(captureParams);
        }

        // 基础AJAX配置
        let ajaxConfig = {
            type: interfaceConfig.type,
            url: url,
            data: captureParams,
            traditional: interfaceConfig.traditional,
            contentType: interfaceConfig.contentType,
            dataType: interfaceConfig.dataType,
            jsonp: interfaceConfig.jsonp,
            // 请求头设置
            beforeSend: interfaceConfig.beforeSend,
            callback: captureCallback,
            errCallback: captureErrCallback,
            _counter: counter++,
            fakeHeaders: interfaceConfig.fakeHeaders
        };

        // 生成请求标记
        ajaxConfig.requestMark = this.generateDataMark({
            ajaxConfig: ajaxConfig,
            params: captureParams
        });

        return ajaxConfig;
    },
    /**
     * 生成接口数据交换操作函数
     * @this module:InterfaceProxy
     * @param key 单个接口配置
     * @param proxyObj 接口代理对象配置
     * @returns {Function}
     * @ignore
     */
    buildInterfaceMethod: function (key, proxyObj) {

        /**
         * 构造接口调用函数
         * @param {*=} params        - 查询参数
         * @param {*=} callback      - 成功回调
         * @param {*=} errCallback   - 失败回调
         * @param {*=} promise       - 同步标记
         */
        return function (params, callback, errCallback, promise) {

            // 根据各级配置生成最终配置
            let interfaceConfig = toolkit.getInterfaceConfig(key, proxyObj);

            // 根据参数和接口配置生成ajaxConfig
            let ajaxConfig = toolkit.getAjaxConfig(arguments, interfaceConfig);

            // 包装errCallback
            let wrapErrCallback = function () {
                /**
                 * 默认失败回调
                 * 1. 如果没有配置通用回调和专用回调，则使用默认回调
                 * 2. 如果配置了通用回调，没有专用回调，则使用通用回调
                 * 3. 如果配置了通用回调和专用回调，则先调用专用回调，专用回调返回true则继续调用通用回调
                 * 4. 如果配置了通用回调和专用回调，则先调用专用回调，专用回调返回false，则过程结束
                 */
                // 在存在配置回调 和 默认回调的情况下，包装新的回调函数
                if (ajaxConfig.errCallback && interfaceConfig.errCallback && (ajaxConfig.errCallback != interfaceConfig.errCallback)) {
                    ajaxConfig.errCallback = (function (spCallback, baseCallback) {
                        return function () {
                            spCallback.apply(this, arguments) && baseCallback.apply(this, arguments);
                        };
                    })(ajaxConfig.errCallback, interfaceConfig.errCallback);
                }
                // 无参数回调 则先使用默认回调作为ajax回调
                if (!ajaxConfig.errCallback) {
                    ajaxConfig.errCallback = interfaceConfig.errCallback;
                }
            };

            // 封装异步链式调用对象
            let agent = {};

            // .then
            agent.then = function (callback,errCallback) {
                if(callback){
                    var _captureCallback = ajaxConfig.callback;
                    ajaxConfig.callback = function () {
                        _captureCallback && _captureCallback.apply(this, arguments);
                        callback.apply(this, arguments);
                    };
                }
                if(errCallback){
                    // ajax回调 = 默认回调 则 参数回调不存在
                    // ajax回调替换为 链式回调
                    if (ajaxConfig.errCallback === interfaceConfig.errCallback) {
                        ajaxConfig.errCallback = errCallback;
                    } else {
                        // 参数回调 默认回调 都存在则先执行参数回调再执行链式回调
                        var _captureErrCallback = ajaxConfig.errCallback;
                        ajaxConfig.errCallback = function () {
                            return (_captureErrCallback && _captureErrCallback.apply(this, arguments))
                                || errCallback.apply(this, arguments);
                        };
                        wrapErrCallback();
                    }
                }
                return agent;
            };

            // .catch .error
            agent.catch = agent.error = function (errCallback) {
                agent.then(null,errCallback);
                return agent;
            };

            // .finally
            agent.finally = function (callback) {
                var _captureCallback = ajaxConfig.callback;
                var _captureErrCallback = ajaxConfig.errCallback;
                ajaxConfig.callback = function () {
                    _captureCallback && _captureCallback.apply(this, arguments);
                    callback.apply(this, arguments);
                };
                ajaxConfig.errCallback = function () {
                    _captureErrCallback && _captureErrCallback.apply(this, arguments);
                    callback.apply(this, arguments);
                };
                ajaxConfig.finallyCallback = callback;
                return {};
            };

            // errCallback
            wrapErrCallback();

            // .loadingSwitch
            agent.loadingSwitch = function (target, key) {
                let _target = target, _key = key;
                interfaceConfig.optLoadingSwitch = function (value) {
                    _target[_key] = value;
                };
                return agent;
            };

            // do ajax => async start
            setTimeout(function () {
                toolkit.doAjax(interfaceConfig, ajaxConfig);
            }, 1);

            return agent;
        };
    },
    // 发起请求
    doAjax(interfaceConfig, ajaxConfig) {
        // Ajax 开始前 interceptor
        if (this.beforeAjax(interfaceConfig, ajaxConfig)) return;
        // 请求响应统一处理
        let onResponse = (type, data) => {
            // Ajax 结束后 interceptor
            if (this.afterAjax(interfaceConfig, ajaxConfig)) return;
            let resMap = {
                success: data => {
                    this.AjaxGetSuccessProcess({
                        data: data.data,
                        status: data.status,
                    }, interfaceConfig, ajaxConfig);
                },
                localSuccess: data => {
                    this.AjaxGetSuccessProcess({
                        data: data.data,
                        status: data.status,
                        isLocalObtain: true
                    }, interfaceConfig, ajaxConfig);
                },
                error: data => {
                    // 失败回调
                    this.AjaxGetFailProcess(data, interfaceConfig, ajaxConfig);
                }
            };
            resMap[type] && resMap[type](data);
        };
        // 绑定请求处理函数
        ajaxConfig.success = function (data, status) {
            onResponse('success', {
                data: data,
                status: status
            });
        };
        ajaxConfig.error = function (status, errorType, describe) {
            onResponse('error', {
                status: status,
                errorType: errorType,
                describe: describe
            });
        };
        // ------------------------------ 开始请求数据 ----------------------------
        // 尝试搜索本地数据
        let hasBeenFound = false;
        if (interfaceConfig.validTime) {
            let localData = this.getAlreadyObtain(ajaxConfig.requestMark);
            if (localData) {
                // 匹配到本地数据
                onResponse('localSuccess', {
                    data: localData,
                    status: 200
                });
                hasBeenFound = true;
            }
        }
        // ajax 获取数据
        if (!hasBeenFound) {
            // AJAX 代理跨域模式 iFrame
            // 条件 :  dataType非[jsonp] & 调用为跨域状态 & 存在跨越代理url配置
            let crossOriginSrc = interfaceConfig.crossOriginSrc || window.__crossOriginSrc__;
            if (interfaceConfig.ajaxIframeAgencyDisable !== false
                && ajaxConfig.dataType.toLocaleLowerCase() !== 'jsonp'
                && parseURL(ajaxConfig.url).origin != location.origin
                && crossOriginSrc) {
                // 跨域处理
                console.log("-------侦测到无法完成的跨域POST请求[" + (interfaceConfig.name || interfaceConfig.pathname) + "]--尝试使用代理跨域模式");
                AjaxAgency({
                    crossOriginSrc: crossOriginSrc,
                    ajaxOptions: ajaxConfig
                });
            } else {
                // AJAX 普通模式
                (interfaceConfig.ajax || Ajax || (window.$ || {}).ajax)(ajaxConfig);
            }
        }
    },
    // 请求前
    beforeAjax(interfaceConfig, ajaxConfig) {
        let flag = this.requestManager.start(interfaceConfig, ajaxConfig);
        if (!flag) {
            // 加载中动画开始
            if (interfaceConfig.loading) {
                interfaceConfig.loadingShow && interfaceConfig.loadingShow(isString(interfaceConfig.loading) ? interfaceConfig.loading : null);
            }
            // 加载中开关
            if (isFunction(interfaceConfig.optLoadingSwitch)) {
                interfaceConfig.optLoadingSwitch(true);
            }
        }
        return flag;
    },
    // 请求后
    afterAjax(interfaceConfig, ajaxConfig) {
        // 加载中动画结束
        if (interfaceConfig.loading) {
            interfaceConfig.loadingHide && interfaceConfig.loadingHide(isString(interfaceConfig.loading) ? interfaceConfig.loading : null);
        }
        // 加载中开关
        if (isFunction(interfaceConfig.optLoadingSwitch)) {
            interfaceConfig.optLoadingSwitch(false);
        }
        return this.requestManager.finish(interfaceConfig, ajaxConfig);
    },
    /**
     * 根据名称&ID获取URL字符串
     * @this module:InterfaceProxy
     * @param interfaceConfig
     * @returns {*}
     * @ignore
     */
    loadUrlString: function (interfaceConfig) {
        // 变量交换
        let urlData = interfaceConfig;
        if (urlData.url) return urlData.url;
        if (urlData.origin) {
            if (!urlData.origin.match(/\/$/) && !urlData.pathname.match(/^\//)) {
                return urlData.origin + '/' + urlData.pathname;
            }
            return urlData.origin + urlData.pathname;
        }
        else return urlData.pathname;
    },
    /**
     * 过滤&转换参数
     * @param params
     * @returns {*}
     * @this module:InterfaceProxy
     * @ignore
     */
    paramFilter: function (params) {
        let result = {};
        if (isArray(params)) {
            return params;
        }
        if (isObject(params)) {
            for (let n in params) {
                if (!params.hasOwnProperty(n)) continue;
                if (params[n] === null) {
                    continue;
                }
                result[n] = params[n];
            }
            return result;
        }
        return null;
    },
    /**
     * Ajax数据预处理
     * 200+>接收数据处理:成功
     * @param response
     * @param {} response.data
     * @param {} response.data.returnCode
     * @param {} response.data.returnMsg
     * @param interfaceConfig
     * @param ajaxConfig
     * @this module:InterfaceProxy
     * @ignore
     */
    AjaxGetSuccessProcess: function (response, interfaceConfig, ajaxConfig) {
        // 基础配置
        let opt = objCover({}, response);

        // 配置项转化
        let data = opt.data;
        let status = opt.status;
        let callback = ajaxConfig.callback;
        let errCallback = ajaxConfig.errCallback;
        let isLocalObtain = opt.isLocalObtain;

        // 数据缓存处理
        !isLocalObtain && interfaceConfig.validTime ? this.pushAlreadyObtain(ajaxConfig, data, interfaceConfig.validTime) : 0;

        // callback
        let callbackEx = null;
        if (callback) {
            callbackEx = function (extractData) {
                // 提取数据
                arguments[0] = interfaceConfig.dataPath ? getObjAttr(extractData, interfaceConfig.dataPath) : arguments[0];
                arguments[0] = isFunction(interfaceConfig.dataFilter) ? interfaceConfig.dataFilter(arguments[0], interfaceConfig.dataType) : arguments[0];
                return callback.apply(null, arguments);
            };
        }

        /*----------------------------------------------------
         接口返回值类型（200）
         > 空 : 视为成功
         > {result:success,...}
         | {success:true,...}
         | { data:?,.. } | { msg:?,..}
         判断后进行成功或失败回调
         > "{result:..}" |...    : 字符串返回，解析成json进行判断回调
         > [{result:}]   |...    : 数组返回，提取内部对象进行判断回调
         > data | msg | page ... : 统一整理后当做回调成功数据进行判断回调
         ----------------------------------------------------*/

        // 当返回数据为空 > 直接当做成功
        if (!data) {
            return callbackEx && callbackEx(null);
        }

        // returnCode & returnMsg 处理
        if (typeof data.returnCode != 'undefined') {
            if (data.returnCode == 1) {
                return callbackEx && callbackEx(data.data, data.returnMsg);
            }
            let returnCodeMap = {
                '-2': "没有权限访问",
                '-1': "登录状态(session)失效",
                '0': "失败",
                '1': "成功",
                '2': "数据格式不正确",
                '3': "参数格式不正确"
            };
            if (typeof data.returnMsg != "undefined") {
                return errCallback && errCallback(data.returnMsg, data.returnCode);
            } else if (returnCodeMap[data.returnCode]) {
                return errCallback && errCallback(returnCodeMap[data.returnCode], data.returnCode);
            } else {
                return errCallback && errCallback("unknownCode", data.returnCode);
            }
        }

        // 当返回数据包含success且为true > 提取返回数据
        if (typeof data.success != 'undefined' && (data.success == true || data.success == 'true')) {
            if (typeof data.data != 'undefined') {
                // 包含data为字符串> 转换成json对象
                if (isString(data.data)) {
                    eval("data.data = " + data.data);
                }
                // 包含 data & msg
                if (data["msg"] || data["message"]) {
                    return callbackEx && callbackEx(data.data, data["msg"] || data["message"], status);
                }
                // 包含 data
                return callbackEx && callbackEx(data.data, data, status);
            } else {
                // 包含 [data]
                for (let n in data) {
                    if (!data.hasOwnProperty(n)) continue;
                    if (n != 'result' && n != 'msg') {
                        return callbackEx && callbackEx(data[n], data, status);
                    }
                }
            }
            return callbackEx && callbackEx(null);
        }

        // 当返回数据包含status且为0 > 视为成功
        if (typeof data.status != "undefined" && (data.status === 0 || data.status === '0')) {
            return callbackEx && callbackEx(data.result || data.data, data, status);
        }

        // 重新对 result='success' 形式提供支持
        if (typeof data.result !== 'undefined' && (data.result === 'success')) {
            return callbackEx && callbackEx(data.data || data, data, status);
        }

        // 当返回数据不包含result > 视为成功
        if (typeof data.success === 'undefined' && typeof data.result === 'undefined') {
            return callbackEx && callbackEx(data, data);
        }

        // 其他情况 > 视为失败
        if (errCallback) {
            // AJAX 失败
            if (data["msg"] || data["message"]) {
                errCallback(data["msg"] || data["message"], data);
            } else {
                errCallback('暂无错误信息,msg(undefined)', data);
            }
        }
    },
    /**
     * 200X>接收数据处理:失败
     * @param errorInfo
     * @param interfaceConfig
     * @param ajaxConfig
     * @this module:InterfaceProxy
     * @ignore
     */
    AjaxGetFailProcess: function (errorInfo, interfaceConfig, ajaxConfig) {
        let errText = "ajax error";
        let errorType = errorInfo.errorType;
        let status = errorInfo.status.status;
        // 处理404
        if (errorType == "error" && status == "404") {
            errText = "404 不可用的路径"
        } else if (errorType == "parsererror") {
            // 解析异常，
            // json 解析失败，尝试作为 HTML 解析
            if (errorInfo.status.response) {
                alert(errorInfo.status.response);
                errText = "JSON返回数据格式异常！";
            } else {
                errText = "服务器异常，返回数据无法解析！";
                if (interfaceConfig.loginTip) {
                    if (!InterfaceProxy.loginTipState) {
                        InterfaceProxy.loginTipState = true;
                        if (isFunction(interfaceConfig.loginTip)) {
                            interfaceConfig.loginTip();
                        }
                    }
                    isFunction(ajaxConfig.finallyCallback) && ajaxConfig.finallyCallback(errText);
                    return;
                }
            }
        } else {
            errText = "服务器异常！";
        }
        // AJAX 失败
        ajaxConfig.errCallback && ajaxConfig.errCallback(errText, errorInfo);
    },
    /**
     * 生成数据标记
     * @param fromInfo
     * @returns {string}
     * @this module:InterfaceProxy
     * @ignore
     */
    generateDataMark: function (fromInfo) {
        let dataMark = "", an;
        for (an in fromInfo.ajaxConfig) {
            if (!fromInfo.ajaxConfig.hasOwnProperty(an)) continue;
            if (isFunction(fromInfo.ajaxConfig[an]) || isObject(fromInfo.ajaxConfig[an])) continue;
            dataMark += an;
            dataMark += fromInfo.ajaxConfig[an];
        }
        for (an in fromInfo.params) {
            if (!fromInfo.params.hasOwnProperty(an)) continue;
            if (an == 'timeStamp' || an == '_') {
                continue;
            }
            dataMark += an;
            dataMark += fromInfo.params[an];
        }
        dataMark = Hashes.hex_hmac(dataMark, '888');
        return dataMark;
    },
    /**
     * 保存数据
     * @param ajaxConfig
     * @param data
     * @param validTime
     * @this module:InterfaceProxy
     * @ignore
     */
    pushAlreadyObtain: function (ajaxConfig, data, validTime) {
        this.repository.push({
            key: ajaxConfig.requestMark,
            timeStamp: +new Date(),
            data: data,
            validTime: validTime
        });
    },
    /**
     * 获得数据
     * @param key
     * @returns {*}
     * @this module:InterfaceProxy
     * @ignore
     */
    getAlreadyObtain: function (key) {
        let data = this.repository.get(key);
        if (data) {
            if ((new Date().getTime() - data.timeStamp) / 1000 > data.validTime) {
                this.repository.remove(key);
                return false;
            }
            return data.responseData;
        }
        return null;
    },
    // 请求队列
    requestManager: (function () {
        function RequestManager() {
            // 写点啥呢...
        }

        // var F = function () {};
        // F.prototype = Array.prototype;
        RequestManager.prototype = [];
        RequestManager.prototype.constructor = RequestManager;
        RequestManager.prototype.requestQueryEqual = (a, b) => a.interfaceConfig == b.interfaceConfig && objEqual(a.ajaxConfig.data, b.ajaxConfig.data);
        RequestManager.prototype.requestEqual = (a, b) => a.interfaceConfig == b.interfaceConfig && a.ajaxConfig.data == b.ajaxConfig.data;
        RequestManager.prototype.removeItem = function (interfaceConfig, ajaxConfig) {
            let removed = null;
            this.find((r, index) => {
                if (interfaceConfig && ajaxConfig) {
                    if (this.requestEqual(r, {
                            interfaceConfig: interfaceConfig,
                            ajaxConfig: ajaxConfig
                        })) {
                        removed = r;
                        this.splice(index, 1);
                        return true;
                    }
                } else if (interfaceConfig && r.interfaceConfig == interfaceConfig) {
                    removed = r;
                    this.splice(index, 1);
                    return true;
                }
            });
            return removed;
        };
        RequestManager.prototype.start = function (interfaceConfig, ajaxConfig) {
            // 顺序校正
            this.sort((a, b) => {
                return a.ajaxConfig._counter > b.ajaxConfig._counter
            });
            // 唯一
            if (interfaceConfig.single) {
                this.removeItem(interfaceConfig);
            }
            // 合并
            if (interfaceConfig.merge) {
                const sameRequest = this.find(r => this.requestQueryEqual(r, {
                    interfaceConfig: interfaceConfig,
                    ajaxConfig: ajaxConfig
                }));
                if (sameRequest) {
                    // 异步执行包装回调的代码 Reason : .then .error .finally
                    const callback = sameRequest.ajaxConfig.callback,
                        errCallback = sameRequest.ajaxConfig.errCallback;
                    if (callback) {
                        sameRequest.ajaxConfig.callback = function () {
                            callback.apply(this, arguments);
                            ajaxConfig.callback.apply(this, arguments);
                        }
                    }
                    if (errCallback) {
                        sameRequest.ajaxConfig.errCallback = function () {
                            errCallback.apply(this, arguments);
                            ajaxConfig.errCallback.apply(this, arguments);
                        }
                    }
                    return true;
                }
            }
            // 入队
            this.push.call(this, {
                interfaceConfig: interfaceConfig,
                ajaxConfig: ajaxConfig,
                timeStamp: new Date()
            });
        };
        RequestManager.prototype.finish = function (interfaceConfig, ajaxConfig) {
            // 排队
            if (interfaceConfig.queue) {
                // 找到同接口存在的前一个请求
                let r = null;
                this.find(request => {
                    if (this.requestEqual(request, {interfaceConfig: interfaceConfig, ajaxConfig: ajaxConfig})) {
                        return true;
                    }
                    if (request.interfaceConfig == interfaceConfig && request.ajaxConfig != ajaxConfig) {
                        r = request;
                    }
                });
                if (r) {
                    const callback = r.ajaxConfig.callback, errCallback = r.ajaxConfig.errCallback;
                    const nextCallback = ajaxConfig.callback, nextErrCallback = ajaxConfig.errCallback;
                    let next;
                    ajaxConfig.callback = function () {
                        const arg = objClone(arguments);
                        next = function () {
                            nextCallback.apply(this, arg);
                        };
                    };
                    ajaxConfig.errCallback = function () {
                        const arg = objClone(arguments);
                        next = function () {
                            nextErrCallback.apply(this, arg);
                        };
                    };
                    if (callback) {
                        r.ajaxConfig.callback = function () {
                            callback.apply(this, arguments);
                            next && next();
                        };
                    }
                    if (errCallback) {
                        r.ajaxConfig.errCallback = function () {
                            errCallback.apply(this, arguments);
                            next && next();
                        };
                    }
                }
            }
            // 清除
            if (!this.removeItem(interfaceConfig, ajaxConfig)) {
                // single : 如果请求已从队列中移除，则终止处理流程
                if (interfaceConfig.single) return true;
            }
        };
        return new RequestManager();
    })(),
    // 持久化存储
    repository: (function () {
        let ls = window.localStorage;
        if (ls) {
            // 历史缓存清除
            for (var i = 0; i < ls.length; i++) {
                let key = ls.key(i);
                if (/^[a-z,0-9]{40}$/.test(key)) {
                    try {
                        let data = JSON.parse(ls.getItem(key));
                        if ((new Date().getTime() - data.timeStamp) / 1000 > data.validTime) {
                            ls.removeItem(key)
                        }
                    } catch (msg) {
                        console.warn("接口过期缓存数据清除失败 : ", msg);
                    }
                }
            }
            return {
                push: function (params) {
                    // 保存新的数据
                    ls[params.key] = JSON.stringify({
                        timeStamp: params.timeStamp,
                        validTime: params.validTime,
                        responseData: params.data
                    });
                },
                get: function (key) {
                    this.clean();
                    return JSON.parse(ls.getItem(key) || 'null');
                },
                remove: function (key) {
                    return ls.removeItem(key);
                },
                // 清理过期缓存
                clean: function () {
                    // 清除过期缓存
                    for (let i = 0; i < ls.length; i++) {
                        // 42db81629a 3582822d6b b6b83bbe1d 2378e6aeeb
                        if (/\w{40}/.test(ls.key(i))) {
                            let storeData = JSON.parse(ls.getItem(ls.key(i)) || 'null');
                            if (storeData) {
                                if ((new Date().getTime() - storeData.timeStamp) / 1000 > storeData.validTime) {
                                    ls.removeItem(ls.key(i));
                                }
                            }
                        }
                    }
                }
            };
        }
        return {
            dataStore: {},
            push: function (params) {
                this.dataStore[params.key] = {
                    timeStamp: params.timeStamp,
                    validTime: params.validTime,
                    responseData: params.data
                };
            },
            get: function (key) {
                return this.dataStore[key] || null;
            },
            remove: function (key) {
                return delete this.dataStore[key];
            }
        };
    })()
};



/**
 * 接口管理器
 * <p>
 *      new InterfaceProxy(interfaceConfig，additionalOptions) 来生成接口操作对象(return)
 *      <br/>示例：
 *      <div><img width="200" src="../img/interfaceInstance.png"/><img width="210" src="../img/interfaceInstance2.png"/></div>
 *      会自动生成一个全局变量 $interface 内容如上图，如果使用第二个参数进行配置的话，会生成对象名称的全局对象，如果配置为false的话会不生成全局对象。
 *
 * </p>
 * @module InterfaceProxy
 * @param {object}          configMap 接口描述配置信息unitInfo[]
 * @param {array|object=}   additionalOptions 接口基础配置信息
 * @param {object}          [additionalOptions.origin=null] 默认域名
 * @param {object}          [additionalOptions.jsonp="jsonpCallback"] 默认jsonp回调函数名称
 * @param {object}          [additionalOptions.errCallback=null] 默认失败回调函数
 * @param {object}          [additionalOptions.ajax=null] ajax 调用
 * @param {function|string} [additionalOptions.loginTip=null] 登录提示
 * @param {function}        [additionalOptions.beforeSend=] 登录提示
 * @param {function}        [additionalOptions.loadingShow=] 显示loading
 * @param {function}        [additionalOptions.loadingHide=] 隐藏loading
 * @param {object}          [additionalOptions.model=] 接口模型
 * @constructor
 */
function InterfaceProxy(configMap, additionalOptions) {
    // 基础配置信息
    this.$additionalOptions = (isArray(additionalOptions) ? additionalOptions : [additionalOptions||{}] ).reduce((obj1,obj2)=>{
        return objCover(objClone(obj1),obj2);
    },{});
    // 接口配置JSON
    this.$configMap = configMap;
    // 装配工具箱
    this.$toolkit = toolkit;
    // model
    this.$model = this.$additionalOptions.model||null;
    // 初始化接口 - 自动生成所有接口操作方法
    this.__init__();
}

InterfaceProxy.prototype = /**@lends module:InterfaceProxy# */{
    // 由接口配置生成方法
    /**
     * 初始化
     * @this module:InterfaceProxy
     * @ignore
     */
    __init__: function () {
        // 变量交换
        let configList = this.$configMap;
        // 提供 iProxy[name] 操作方法 | 将 构造的getData在调用时的this指向getData[pn]本体
        let iProxy = this;
        // 动态生成接口代理对象操作方法
        for (let pn in configList) {
            if (!configList.hasOwnProperty(pn)) continue;
            /**
             * @typedef {function} interfaceMethod      - 自动生成的接口操作方法
             * @property {unitInfo} interfaceInfo       - 接口信息，来源接口配置
             * @property {unitInfo} info                - 接口信息，来源接口配置
             * @property {string} url                   - 接口URL
             * @example
             * this.deleteWareHouse("WH3WE3W3345234ER23",function(data){
             *      // 数据获取成功回调
             *      console.log(data);
             * },function(msg){
             *      // 数据获取失败回调
             *      console.log(msg);
             * })
             * // 默认第一个回调函数为成功，第二个为失败
             * // 如果传入函数有名称(success、error)，则优先以函数名为准
             *
             */
            iProxy[pn] = toolkit.buildInterfaceMethod(pn, this);
            iProxy[pn].$options = iProxy[pn].info = configList[pn];
            iProxy[pn].url = iProxy[pn].info.url || iProxy[pn].info.origin + iProxy[pn].info.pathname;
            /**
             * TODO : 现在可能和ajax请求的url不完全一致
             */
            iProxy[pn].getUrl = (proxyObj => {
                const key = pn;
                return function (query) {
                    const $options = toolkit.getInterfaceConfig(key, proxyObj);
                    var url = $options.url || $options.origin + $options.pathname;
                    var params = objCover(objClone($options.params) || {}, query);
                    return buildURL({url: url, params: params})
                }
            })(this);
        }

        // 包装async
        if (typeof Promise !== "undefined") {
            iProxy.async = {};
            Object.keys(iProxy).forEach(fn => {
                iProxy.async[fn] = function () {
                    let wrapArguments = [].slice.call(arguments);
                    return new Promise((resolve, reject) => {
                        let callback = function () {
                            resolve.apply(this, arguments);
                        };
                        let errCallback = function () {
                            reject.apply(this, arguments);
                        };
                        wrapArguments = wrapArguments.concat([callback, errCallback]);
                        iProxy[fn].apply(this, wrapArguments);
                    });
                }
            });
        }
    }
};

// 导出Hashes
InterfaceProxy.Hashes = Hashes;
// 修改全局默认配置API
InterfaceProxy.setDefault = options => {
    objCover(defaultOptions, options);
};

window.InterfaceProxy = InterfaceProxy;

export default InterfaceProxy;

